import { MailOutlined } from '@ant-design/icons';
import { Alert, Input, Modal, Space, Typography } from 'antd';
import { useCallback, useContext, useMemo, useState } from 'react';

import {
  useLazyTestFileRequestClientsQuery,
  useLazyTestFileRequestRecordsQuery,
  useStartFileRequestClientsMutation,
  useStartFileRequestRecordsMutation,
} from '@app/api/UserFileRequests';
import { CloseIconsForModalWithDataTestId } from '@app/components/CloseIconsForModalWithDataTestId';
import type { NExportDialog } from '@app/components/ExportDialog/ExportDialog.types';
import { FRONT_WARNING, SUCCESS, WARNING } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import { NFiles } from '@app/types/Files';

import { transformHoursToDays } from '@pages/Clients/ClientsList/utils/transformHoursToDays';

const { Text } = Typography;

export const ExportDialog: NExportDialog.Main = ({
  searchId,
  exportDataType = NFiles.ExportDataTypes.Clients,
  titleDataTestId = '',
  modalDataTestId = '',
  descriptionDataTestId = '',
  emailSubjectDataTestId = '',
}) => {
  const { showNotification } = useContext(NotificationContext);
  const { params } = useUserPrint();
  const [testFileRequestClients] = useLazyTestFileRequestClientsQuery();
  const [testFileRequestRecords] = useLazyTestFileRequestRecordsQuery();
  const [startFileRequestClients] = useStartFileRequestClientsMutation();
  const [startFileRequestRecords] = useStartFileRequestRecordsMutation();
  const [isExportModal, setExportModal] = useState(false);
  const [emailToSendFile, setEmailToSendFile] = useState<string | null>(null);
  const [exportStorageDuration, setExportStorageDuration] = useState<
    string | null
  >(null);
  const [exportWarning, setExportWarning] = useState<string | null>(null);
  const [exportEmailSubject, setExportEmailSubject] = useState<string | null>(
    ''
  );
  const [exportButtonLoader, setExportButtonLoader] = useState(false);
  const [exportCancelDisabling, setExportCancelDisabling] = useState(false);
  const isClientsExport = exportDataType === NFiles.ExportDataTypes.Clients;
  const exportDataTypeTexts = useMemo<Record<NFiles.ExportDataTypes, string[]>>(
    () => ({
      Records: ['записей', 'записи', 'записей'],
      RecordsServices: ['услуг записей', 'услуги записей', 'услуг записей'],
      Clients: ['клиентов', 'клиентов', 'клиентской базы'],
    }),
    []
  );

  const handleExportStart = useCallback(async () => {
    if (searchId && params.ActiveBranchId) {
      try {
        setExportButtonLoader(true);
        setExportCancelDisabling(true);
        setExportModal(false);
        showNotification(
          'Начали формировать отчёт. Отправим вам ссылку на почту, как только он будет готов.',
          SUCCESS
        );

        const subject = exportEmailSubject || '';

        if (isClientsExport) {
          await startFileRequestClients({
            ActiveBranchId: params.ActiveBranchId,
            searchId,
            subject,
          }).unwrap();
        } else {
          await startFileRequestRecords({
            ActiveBranchId: params.ActiveBranchId,
            searchId,
            subject,
            groupServices: exportDataType === NFiles.ExportDataTypes.Records,
          }).unwrap();
        }
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      } finally {
        setExportEmailSubject('');
        setExportButtonLoader(false);
        setExportCancelDisabling(false);
      }
    }
  }, [
    searchId,
    params.ActiveBranchId,
    exportEmailSubject,
    exportDataType,
    isClientsExport,
    startFileRequestClients,
    startFileRequestRecords,
    showNotification,
  ]);

  const checkDownloadProbability = async () => {
    if (searchId && params.ActiveBranchId) {
      try {
        const testFileRequestArgs = {
          ActiveBranchId: params.ActiveBranchId,
          searchId,
        };
        const data = isClientsExport
          ? await testFileRequestClients(testFileRequestArgs).unwrap()
          : await testFileRequestRecords(testFileRequestArgs).unwrap();

        if (data.CanPrepare) {
          setExportModal(true);
          setEmailToSendFile(data.Email || null);
          setExportStorageDuration(transformHoursToDays(data.LifetimeHours));
          setExportWarning(data.Warning ?? null);
        } else {
          Modal.error({
            title: `Нельзя выгрузить ${exportDataTypeTexts[exportDataType][1]}`,
            content: data.ErrorMessage || '',
            okText: 'Закрыть',
          });
        }
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    } else {
      showNotification('Не найден Search Id', FRONT_WARNING);
    }
  };

  const handleEmail = (email: string | null) => {
    if (email) {
      return email;
    } else {
      return 'ваш email';
    }
  };

  const handleCancel = () => {
    setExportEmailSubject('');
    setExportModal(!isExportModal);
  };

  return (
    <>
      {exportDataType === 'Records' ? (
        <span onClick={checkDownloadProbability} data-testid="export-records">
          Записи
        </span>
      ) : null}
      {exportDataType === 'RecordsServices' ? (
        <span
          onClick={checkDownloadProbability}
          data-testid="export-records-services"
        >
          Услуги записей
        </span>
      ) : null}
      {exportDataType === 'Clients' ? (
        <span onClick={checkDownloadProbability} data-testid="export-to-excel">
          Выгрузить в Excel
        </span>
      ) : null}
      <Modal
        title={generateSpanWithDataTestId(
          `Выгрузка ${exportDataTypeTexts[exportDataType][2]}`,
          titleDataTestId
        )}
        data-testid={modalDataTestId}
        open={isExportModal}
        okText="Отправить на почту"
        closeIcon={<CloseIconsForModalWithDataTestId />}
        okButtonProps={{
          icon: <MailOutlined />,
          loading: exportButtonLoader,
          'data-testid': 'export-button',
        }}
        cancelButtonProps={{
          disabled: exportCancelDisabling,
          'data-testid': 'cancel-export',
        }}
        onOk={handleExportStart}
        onCancel={handleCancel}
      >
        <Space
          direction="vertical"
          size="middle"
          style={{ width: '100%' }}
          data-testid="export-popup-description"
        >
          <Text>
            {`Ссылка на выгрузку ${exportDataTypeTexts[exportDataType][2]} будет отправлена на `}
            <Text strong>{handleEmail(emailToSendFile)}</Text>.
          </Text>
          {exportStorageDuration && (
            <Text data-testid={descriptionDataTestId}>
              Обратите внимание, что отчёт будет доступен в течение
              {` ${exportStorageDuration} `}с момента формирования. По истечении
              этого срока файл будет удалён.
            </Text>
          )}
          {exportWarning && (
            <Alert
              className="export-dialog-warning-icon"
              message={exportWarning}
              type="warning"
              showIcon
            />
          )}
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong data-testid={emailSubjectDataTestId}>
              Укажите тему письма (опционально)
            </Text>
            <Input
              maxLength={600}
              value={exportEmailSubject || ''}
              onChange={({ target }) =>
                setExportEmailSubject(target.value || '')
              }
              data-testid="email-subject-input"
            />
          </Space>
        </Space>
      </Modal>
    </>
  );
};
